import type { parameterNames } from './constants';

export type Dashboard =
  | 'CLINICAL_MENTORING_ALPHA'
  | 'CLINICAL_MENTORING_BETA'
  | 'ENGAGEMENT'
  | 'PROVIDER_DETAIL'
  | 'HYGIENE_TASKS'
  | 'PRACTICE_DETAIL';

export type EnabledDashboard = 'CLINICAL' | 'HYGIENE' | 'ENGAGEMENT';

export type ParameterName = (typeof parameterNames)[number];

/**
 * Diagram showing basic Viz object interfaces: https://help.tableau.com/current/api/embedding_api/en-us/docs/embedding_api_about.html
 * Event listeners: https://help.tableau.com/current/api/embedding_api/en-us/docs/embedding_api_event.html
 *
 * Viz object can be accessed via the following:
 * const vizElement = document.getElementById(`tableau-viz-${dashboard}`);
 */

export interface Viz extends HTMLElement {
  workbook: {
    name: string;
    activeSheet: Sheet;
    getParametersAsync(): Promise<TableauParameter[]>;
    activateSheetAsync(sheetName: string): Promise<void>;
  };
}

interface Sheet {
  index: number;
  name: string;
}

export const TableauEventType = {
  FirstInteractive: 'firstinteractive',
  VizLoadError: 'vizloaderror',
  IframeSrcUpdated: 'iframesrcupdated',
  MarkSelectionChanged: 'markselectionchanged',
  FilterChanged: 'filterchanged',
  ParameterChanged: 'parameterchanged',
  TabSwitched: 'tabswitched',
} as const;

export interface VizLoadErrorEvent {
  detail: {
    errorCode: string;
    message: string; // JSON string
  };
}

export interface ParameterChangedEvent {
  detail: {
    getParameterAsync(): Promise<TableauParameter>;
  };
}

interface TableauParameter {
  parameterImpl: {
    currentValue: {
      value: string;
    };
    name: string;
  };
}

export interface FilterChangedEvent {
  detail: {
    getFilterAsync(): Promise<TableauFilter>;
  };
}

export interface TabSwitchedEvent {
  detail: {
    newSheetName: string;
  };
}

interface TableauFilter {
  appliedValues?: {
    value: string;
  }[];
  fieldName: string;
  isAllSelected: boolean;
}

export interface TableauFilterState {
  appliedValues: string[];
  isAllSelected: boolean;
}

export interface TableauMarkSelectionEvent {
  detail: {
    getMarksAsync(): Promise<{ data: TableauMark[] }>;
  };
}

interface TableauMark {
  columns: TableauColumn[];
  data: TableauMarkData[][];
}

interface TableauColumn {
  fieldName: string;
}

interface TableauMarkData {
  value: string;
}
